import { Injectable } from '@angular/core';
import { Job, JobItem } from '../models';
import { RepoJobsService } from '../repositories/repo-jobs.service';

@Injectable()
export class JobItemFactory {
    items: JobItem[] = [];
    allItems: JobItem[] = [];
    job?: Job;

    constructor(private repoJobs: RepoJobsService) {}

    load(j: Job, i: JobItem[] | undefined) {
        if (i) this.allItems = i;
        this.items = [];
        this.job = j;
        this.generateItems();
        this.order();
    }

    private generateItems() {
        // Create hash lookup from ID_JobItem to item object
        const lookup = new Map<string, JobItem>();
        this.allItems.forEach((i) => {
            if (!i.IsDeleted) {
                i.children = [];
                lookup.set(i.ID_JobItem, i);
            }
        });

        // Create list of children for each parent item
        const parents = new Map<string | null, JobItem[]>();
        this.allItems.forEach((i) => {
            if (!i.IsDeleted) {
                let parentID: string | null = i.ParentId;

                let children = parents.get(parentID);
                if (children === undefined) {
                    children = [];
                    parents.set(parentID, children);
                }

                children.push(i);
            }
        });

        // Sort and assign children to parents
        parents.forEach((children, parentId) => {
            children.sort((a: JobItem, b: JobItem) => a.OrderNumber - b.OrderNumber);
            if (!parentId) {
                children.forEach((i: JobItem) => this.items.push(i));
            } else {
                const parentItem = lookup.get(parentId);
                if (parentItem) {
                    parentItem.children = children;

                    if (parentItem.ParentId === null) {
                        parentItem.calculateTotals();
                    } else {
                        parentItem.selected = false;
                        parentItem.calculateVariance();
                    }
                }
            }
        });
    }

    order() {
        let orderNumber = 0;
        let parentOrder = 0;
        let childOrder = 0;
        let recipeOrder = 0;

        for (let i = 0; i < this.items.length; i++) {
            const parent = this.items[i];
            childOrder = 0;
            if (parent && !parent.IsDeleted) {
                orderNumber++;
                parentOrder++;
                parent.OrderNumber = orderNumber;
                parent.DisplayedOrderNumber = parentOrder.toString();

                if (!parent.children) parent.children = [];

                for (let c = 0; c < parent.children.length; c++) {
                    const child = parent.children[c];
                    recipeOrder = 0;
                    if (child && !child.IsDeleted) {
                        orderNumber++;
                        childOrder++;
                        child.OrderNumber = orderNumber;
                        child.DisplayedOrderNumber = parent.DisplayedOrderNumber + '.' + childOrder.toString();

                        if (!child.children) child.children = [];

                        for (let r = 0; r < child.children.length; r++) {
                            const recipeItem = child.children[r];

                            if (recipeItem && !recipeItem.IsDeleted) {
                                orderNumber++;
                                recipeOrder++;
                                recipeItem.OrderNumber = orderNumber;
                                recipeItem.DisplayedOrderNumber = child.DisplayedOrderNumber + '.' + recipeOrder.toString();
                            }
                        }
                    }
                }
            }
        }
    }

    changeCategoryOrder(oldIndex: number, newIndex: number) {
        const item = this.items[oldIndex];

        if (!item) return;

        this.items.splice(oldIndex, 1);
        this.items.splice(newIndex, 0, item);
        this.order();
    }

    changeItemOrder(parent: JobItem, oldIndex: number, newIndex: number) {
        if (!parent || !parent?.children?.length) return;

        const item = parent.children[oldIndex];

        if (!item) return;

        parent.children.splice(oldIndex, 1);
        parent.children.splice(newIndex, 0, item);
        this.order();
    }

    deleteCategory(item: JobItem) {
        item.IsDeleted = true;

        if (!item.children) return;

        item.children.forEach((childItem) => {
            childItem.IsDeleted = true;
            if (!childItem.children) return;
            childItem.children.forEach((recipeItem) => {
                recipeItem.IsDeleted = true;
            });
        });
        const index = this.items.indexOf(item);
        this.items.splice(index, 1);
        this.order();
    }

    deleteItem(item: JobItem) {
        item.IsDeleted = true;
        this.order();
    }

    addCategory() {
        if (!this.job) return;
        const jobItem = this.items.length ? this.items[this.items.length - 1] : null;
        const orderNumber = jobItem ? jobItem.OrderNumber + 1 : 1;
        const displayedOrderNumber = jobItem ? jobItem.DisplayedOrderNumber + 1 : 1;
        const defaults = {
            ID_Job: this.job.ID_Job,
            Description: 'New Category',
            DisplayedOrderNumber: displayedOrderNumber,
            OrderNumber: orderNumber,
        };

        const item = this.repoJobs.CreateJobItem(this.job, defaults);
        item.children = [];
        item.MatchesSearchText = true;
        this.items.push(item);
        this.order();
        return item;
    }

    addItem(parent: JobItem) {
        if (!this.job) return;
        const defaults = {
            ID_Job: this.job.ID_Job,
            ParentId: parent.ID_JobItem,
            Description: 'New Item',
        };

        const childItem = this.repoJobs.CreateJobItem(this.job, defaults);
        childItem.MatchesSearchText = true;

        if (parent.children) {
            parent.children.push(childItem);
        }

        this.order();
        return childItem;
    }
}
